import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Trash2, ShieldAlert, AlertTriangle, Info, FileSearch } from "lucide-react";
import { API } from "../../context/AuthContext";
import GapAudit from "./GapAudit";

const SPOUSE_BADGE = {
  husband: "text-sky-400 bg-sky-500/10 border-sky-500/30",
  wife: "text-orange-400 bg-orange-500/10 border-orange-500/30",
  joint: "text-purple-300 bg-purple-500/10 border-purple-400/30",
  unknown: "text-gray-400 bg-white/5 border-white/10",
};

const SEV = {
  critical: { cls: "border-rose-500/40 bg-rose-500/10 text-rose-300", Icon: ShieldAlert },
  warning: { cls: "border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#fbbf24]", Icon: AlertTriangle },
  info: { cls: "border-[#2dd4bf]/40 bg-[#2dd4bf]/10 text-[#2dd4bf]", Icon: Info },
};

function FlagRow({ flag }) {
  const { cls, Icon } = SEV[flag.severity] || SEV.info;
  return (
    <div className={`border rounded-lg px-3 py-2 text-[11px] leading-relaxed ${cls}`} data-testid="intel-flag">
      <div className="flex items-center gap-1.5 font-bold uppercase text-[10px] tracking-wide">
        <Icon className="w-3 h-3 shrink-0" /> {flag.title}
      </div>
      {flag.detail && <p className="mt-1 text-gray-300">{flag.detail}</p>}
    </div>
  );
}

function ProfileCard({ p, onDelete, canEdit }) {
  const [open, setOpen] = useState(false);
  const entries = Object.entries(p.fields || {});
  const shown = open ? entries : entries.slice(0, 4);
  return (
    <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-4 space-y-3 group" data-testid={`intel-profile-${p.profile_id}`}>
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono font-bold uppercase text-[#f59e0b] bg-[#f59e0b]/10 border border-[#f59e0b]/30 px-2 py-0.5 rounded">{p.category_label}</span>
            <span className={`text-[10px] font-mono font-bold uppercase border px-2 py-0.5 rounded ${SPOUSE_BADGE[p.spouse_identity] || SPOUSE_BADGE.unknown}`} data-testid="intel-spouse-badge">{p.spouse_identity}</span>
          </div>
          <p className="text-[10px] text-gray-500 font-mono mt-1 truncate">{p.source_name || "pasted text"} · {String(p.created_at).slice(0, 10)}</p>
        </div>
        {canEdit && (
          <button onClick={() => onDelete(p.profile_id)} data-testid={`intel-delete-${p.profile_id}`} className="text-gray-500 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shrink-0">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {p.flags && p.flags.length > 0 && (
        <div className="space-y-1.5">{p.flags.map((f, i) => <FlagRow key={i} flag={f} />)}</div>
      )}

      <div className="space-y-1">
        {shown.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[minmax(110px,35%)_1fr] gap-2 text-[11px] border-b border-white/5 pb-1">
            <span className="font-mono text-gray-500 uppercase text-[9px] pt-0.5 break-words">{k.replace(/_/g, " ")}</span>
            <span className={`leading-relaxed break-words ${v === "Unknown/Unstated in Text" ? "text-gray-600 italic" : "text-gray-200"}`}>{v}</span>
          </div>
        ))}
        {entries.length > 4 && (
          <button onClick={() => setOpen(!open)} data-testid="intel-toggle-fields" className="text-[10px] font-mono text-[#2dd4bf] hover:text-[#5eead4] transition-colors duration-200">
            {open ? "SHOW LESS" : `SHOW ALL ${entries.length} FIELDS`}
          </button>
        )}
      </div>
    </div>
  );
}

export default function IntelPanel({ caseId, canEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/intelligence`, { params: { case_id: caseId } })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Couldn't load case intelligence."))
      .finally(() => setLoading(false));
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try {
      await axios.delete(`${API}/intelligence/${id}`);
      setData((d) => ({ ...d, profiles: d.profiles.filter((p) => p.profile_id !== id) }));
      toast.success("Profile removed.");
    } catch {
      toast.error("Couldn't remove that profile.");
    }
  };

  return (
    <div className="space-y-4" data-testid="intel-panel">
      <GapAudit caseId={caseId} canEdit={canEdit} />
      <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-5 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-white uppercase tracking-wide font-display flex items-center gap-2">
            <FileSearch className="w-4 h-4 text-[#f59e0b]" /> Case Intelligence
          </h3>
          <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
            Every ingested document is auto-classified (pay stubs, FL-150s, court orders, MSAs...) and parsed into Husband/Wife dual-track data with strategic flags. Strictly source-bound — nothing is inferred.
          </p>
        </div>
        <button onClick={load} data-testid="intel-refresh-btn" className="text-gray-300 hover:text-[#f59e0b] border border-white/10 p-2 rounded-xl transition-colors duration-200 shrink-0" title="Refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-[#f59e0b] font-mono animate-blink py-12 text-center">Loading document intelligence...</div>
      ) : !data || data.profiles.length === 0 ? (
        <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-8 text-center space-y-2" data-testid="intel-empty">
          <p className="text-xs font-bold text-gray-300 uppercase tracking-wide">No document profiles yet</p>
          <p className="text-[11px] text-gray-400 leading-relaxed max-w-md mx-auto">
            Upload pay stubs, bank statements, FL-150/FL-140 forms, court orders, RFOs, MSAs, or judgments — Professor Flux will classify each one and surface strategic flags here automatically.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 text-[10px] font-mono text-gray-400">
            <span data-testid="intel-profile-count">{data.count} document profile{data.count === 1 ? "" : "s"}</span>
            <span>·</span>
            <span className={data.flag_count > 0 ? "text-rose-400 font-bold" : ""} data-testid="intel-flag-count">{data.flag_count} strategic flag{data.flag_count === 1 ? "" : "s"}</span>
          </div>
          {data.profiles.map((p) => <ProfileCard key={p.profile_id} p={p} onDelete={del} canEdit={canEdit} />)}
        </>
      )}
    </div>
  );
}
